import { useCallback, useEffect, useState } from "react";
import TaskHeader from "components/task/header/TaskHeader";
import TaskContent from "components/task/content/TaskContent";
import { Box, Stack } from "@mui/joy";
import { ProgramContentType } from "data/BlockComponent";
import * as _ from "lodash";
import MeditationRecordList from "./MeditationRecordList";
import { useRecoilState, useRecoilValue } from "recoil";
import { patientAtom } from "recoil/patientAtom";
import { useQuery } from "@tanstack/react-query";
import { userAtom } from "recoil/userAtom";
import useGetCertainEventFromApp from "hooks/useGetCertainEventFromApp";
import useGetMeditationRecordData from "hooks/useGetMeditationRecordData";
import {
  MEDITATION_RECORD_DOMAIN,
  MEDITATION_RECORD_LIST_ENDPOINT,
  meditationRecordApi,
  MeditationRecordType,
} from "api/meditationRecordApi";
import Statistics from "./Statics";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MeditationGroupType } from "api/meditationRecordApi";
import useIsInappWebview from "hooks/useIsInappWebview";
import useOpenNewMeditationRecord from "hooks/useOpenNewMeditationRecord";
import { taskListAtom } from "recoil/taskListAtom";
import { selectedMeditationRecordKeyAtom } from "recoil/selectedMeditationRecordAtom";

export default function MeditationRecord({ taskKey }: { taskKey: string }) {
  const [searchParams] = useSearchParams();
  const currentMeditationRecordKeyParam = searchParams.get("mrk");
  const groupTypeParam = searchParams.get("groupType") as
    | MeditationGroupType
    | null;
  const navigate = useNavigate();

  const patientState = useRecoilValue(patientAtom);
  const user = useRecoilValue(userAtom);
  const taskListState = useRecoilValue(taskListAtom);

  const isCoach = user?.accessToken && user?.role !== "patient";

  const isInappWebview = useIsInappWebview();
  const isTaskContainerSmall = isInappWebview;
  const [data, setData] = useState<ProgramContentType[] | undefined>();

  const patientId = patientState?.patientId;

  const [recordList, setRecordList] = useState<
    MeditationRecordType[] | undefined
  >();

  const [currentRecord, setCurrentRecord] = useState<
    MeditationRecordType | undefined
  >();

  const [selectedMeditationRecordKey, setSelectedMeditationRecordKey] =
    useRecoilState(selectedMeditationRecordKeyAtom);

  const [isEmpty, setIsEmpty] = useState<boolean | undefined>(undefined);

  const [isStatisticsOpen, setIsStatisticsOpen] = useState<boolean>(false);

  const openNewMeditationRecord = useOpenNewMeditationRecord();

  const {
    data: fetchedData,
    refetch,
    isFetching,
  } = useGetMeditationRecordData({
    mrk: currentRecord?.meditationRecordKey,
    isFirst: isEmpty,
    enabled: !!recordList && taskListState.length > 0,
    groupType: groupTypeParam || currentRecord?.type,
  });

  // 1. param이 있을 때 처리하는 useEffect
  useEffect(() => {
    if (!recordList || !patientId) return;

    if (currentMeditationRecordKeyParam) {
      const selectedRecord = recordList.find(
        (element) =>
          element.meditationRecordKey === currentMeditationRecordKeyParam
      );

      if (selectedRecord) {
        setCurrentRecord(selectedRecord);
      } else if (
        currentMeditationRecordKeyParam.includes(patientId.toString())
      ) {
        const newRecord: MeditationRecordType = {
          meditationRecordKey: currentMeditationRecordKeyParam,
          date: new Date().toISOString(),
          title: "",
          isHighlight: false,
          hasAiRecommendation: false,
          durationMinutes: 0,
          isDone: false,
          type: groupTypeParam ?? undefined,
          lastDoneAt: undefined,
          shouldBeDone: false,
        };

        setRecordList((prevList) => [newRecord, ...(prevList || [])]);
        setCurrentRecord(newRecord);
      }

      // param을 처리한 후 URL에서 제거
      navigate(`${window.location.pathname}`, { replace: true });
    }
  }, [recordList, currentMeditationRecordKeyParam, patientId, navigate]);

  // 2. param이 없을 때 첫 번째 기록을 선택하는 useEffect
  useEffect(() => {
    if (
      !recordList ||
      currentMeditationRecordKeyParam ||
      isFetching ||
      isStatisticsOpen
    )
      return;

    if (recordList.length > 0 && !currentRecord) {
      setCurrentRecord(recordList[0]);
    }
  }, [
    recordList,
    currentMeditationRecordKeyParam,
    currentRecord,
    isFetching,
    isStatisticsOpen,
  ]);

  function openStatistics() {
    setIsStatisticsOpen(true);
    setCurrentRecord(undefined);
    setData(undefined);
  }

  //기록 선택하면 통계 닫기
  useEffect(() => {
    if (currentRecord) {
      setIsStatisticsOpen(false);
    }
  }, [currentRecord]);

  //기록 추가한 직후
  useEffect(() => {
    if (
      currentRecord &&
      !recordList?.find(
        (thoughtRecord) =>
          thoughtRecord.meditationRecordKey ===
          currentRecord?.meditationRecordKey
      )
    ) {
      setRecordList((list) => [
        ...(list || []),
        { ...currentRecord, shouldBeDone: false },
      ]);
    }
  }, [currentRecord]);

  // Keep currentRecord synced with the latest list updates (e.g., after invalidations)
  useEffect(() => {
    if (!recordList || recordList.length === 0) return;

    const selectedKey =
      selectedMeditationRecordKey || currentRecord?.meditationRecordKey;
    if (!selectedKey) return;

    const updated = recordList.find(
      (item) => item.meditationRecordKey === selectedKey
    );
    if (updated && !_.isEqual(updated, currentRecord)) {
      setCurrentRecord(updated);
    }
  }, [recordList, selectedMeditationRecordKey]);

  useEffect(() => {
    if (currentRecord?.meditationRecordKey) {
      setSelectedMeditationRecordKey(currentRecord.meditationRecordKey!!);
    }
  }, [currentRecord]);

  useEffect(() => {
    return () => {
      setSelectedMeditationRecordKey(undefined);
    };
  }, []);

  const refetchActivityEvent = useGetCertainEventFromApp({
    eventName: "refetch_activity",
  });

  useEffect(() => {
    if (refetchActivityEvent) {
      refetch();
    }
  }, [refetchActivityEvent]);

  const getRecordListqueryFn = useCallback(
    () => meditationRecordApi.getList(patientState?.patientId!!),
    [patientState]
  );

  useQuery(
    [
      MEDITATION_RECORD_DOMAIN,
      MEDITATION_RECORD_LIST_ENDPOINT,
      patientState?.patientId,
    ],
    getRecordListqueryFn,
    {
      enabled: !!patientState?.patientId,
      onSuccess: (data) => {
        const mapped = data.result?.map((each) => ({
          ...each,
          shouldBeDone: each?.isDone
            ? false
            : recordList?.find(
                (element) =>
                  element.meditationRecordKey === each.meditationRecordKey
              )?.shouldBeDone,
        }));

        if (!mapped || mapped.length === 0) {
          setRecordList(mapped);
          setIsEmpty(true);
          if (!currentRecord) {
            openNewMeditationRecord();
          }
        } else {
          const sorted = mapped
            .slice()
            .sort((a, b) => +new Date(b.date || "") - +new Date(a.date || ""));
          setRecordList(sorted);
          setIsEmpty(false);

          // Always sync currentRecord to the latest list after refresh
          const selectedKey =
            currentMeditationRecordKeyParam ||
            selectedMeditationRecordKey ||
            currentRecord?.meditationRecordKey ||
            sorted[0]?.meditationRecordKey;

          if (selectedKey) {
            const selectedIndex = sorted.findIndex(
              (element) => element.meditationRecordKey === selectedKey
            );
            if (selectedIndex > -1) {
              setCurrentRecord(sorted[selectedIndex]);
            }
          }
        }
      },
    }
  );

  // Clear content data when switching to a different Meditation Record to avoid showing stale content
  useEffect(() => {
    // When the selected MRK changes, clear the content so UI doesn't flicker with previous record's state
    // It will be re-populated once the correct fetchedData arrives
    setData(undefined);
  }, [currentRecord?.meditationRecordKey]);

  // Apply fetched data only when it belongs to the currently selected Meditation Record
  useEffect(() => {
    if (!fetchedData) return;

    const currentMrk = currentRecord?.meditationRecordKey;
    // Some fetch payloads may include the mrk; use it when available to guard against race conditions
    const anyFetched = fetchedData as any;
    const fetchedMrk = anyFetched?.meditationRecordKey ?? anyFetched?.mrk;

    // If both MRKs exist and do not match, ignore this payload (likely a late response for a previously selected record)
    if (fetchedMrk && currentMrk && fetchedMrk !== currentMrk) return;

    if (!_.isEqual(fetchedData.content, data)) {
      setData(fetchedData.content);
    }
  }, [fetchedData, currentRecord?.meditationRecordKey]);

  const [moveToIndex, setMoveToIndex] = useState<number | undefined>(undefined);

  return (
    <>
      <Box
        sx={{
          flex: "none",
          zIndex: 100,
          // px: { xs: "16px", sm: "24px" },
        }}
      >
        <TaskHeader
          key={`${taskKey}_${currentRecord?.meditationRecordKey || "no-mrk"}`}
          taskKey={taskKey}
          meditationRecordKey={currentRecord?.meditationRecordKey}
          // title={`${dayjs(currentRecord?.date).format(
          //   t_common("MMM D일 ddd요일")
          // )}`}
          title={`명상훈련지`}
          contentData={data}
          setContentData={setData}
          setMoveToIndex={setMoveToIndex}
          isDone={currentRecord?.isDone}
          lastDoneAt={currentRecord?.lastDoneAt}
          {...(isTaskContainerSmall && {
            extraContent: (
              <MeditationRecordList
                recordList={recordList}
                currentRecord={currentRecord}
                setCurrentRecord={setCurrentRecord}
                isStatisticsOpen={isStatisticsOpen}
                openStatistics={openStatistics}
              />
            ),
          })}
        />
      </Box>
      <Stack sx={{ flex: 1, overflow: "hidden" }} direction={"row"}>
        {!isTaskContainerSmall && (
          <MeditationRecordList
            recordList={recordList}
            {...(!isStatisticsOpen && { currentRecord: currentRecord })}
            setCurrentRecord={setCurrentRecord}
            isStatisticsOpen={isStatisticsOpen}
            openStatistics={openStatistics}
          />
        )}
        {isStatisticsOpen ? (
          <Statistics />
        ) : (
          <Stack sx={{ flex: 1, overflow: "hidden" }}>
            <TaskContent
              key={`${taskKey}_${currentRecord?.meditationRecordKey || "no-mrk"}`}
              taskKey={taskKey}
              meditationRecordKey={currentRecord?.meditationRecordKey}
              data={data}
              setData={setData}
              moveToIndex={moveToIndex}
              setMoveToIndex={setMoveToIndex}
              isDone={currentRecord?.isDone}
              translationVersion={fetchedData?.translationVersion}
            />
          </Stack>
        )}
      </Stack>
    </>
  );
}
